import React from 'react';
import {
    TeamBase,
    WorkspaceCoreValue,
    WorkspaceMemberWithUser,
} from '@/common/types/databaseTypes';
import { CoreValueSettingsModal } from '@/features/workspace/components/modals';
import { ConfirmationModal } from '@/common/components/modals/ConfirmationModal';
import { useStackModalStore } from '@/common/stores/modalStore';
import CreateProjectModal from '@/features/project/components/CreateProjectModal';

// 모달 매핑 정의
interface ModalRegistry {
    CORE_VALUE_SETTINGS: typeof CoreValueSettingsModal;
    CONFIRMATION: typeof ConfirmationModal;
    CREATE_PROJECT: typeof CreateProjectModal;
}

// 모달 타입 키 타입
export type ModalType = keyof ModalRegistry;

// 모든 모달 컴포넌트 등록
const MODAL_COMPONENTS: ModalRegistry = {
    CORE_VALUE_SETTINGS: CoreValueSettingsModal,
    CONFIRMATION: ConfirmationModal,
    CREATE_PROJECT: CreateProjectModal,
    // 여기에 다른 모달 컴포넌트들을 추가할 수 있습니다.
};

/**
 * 모달 스택을 렌더링하는 컴포넌트
 * 여러 모달을 동시에 표시할 수 있음
 */
export const ModalRenderer = () => {
    // Zustand 스토어에서 모달 스택 가져오기
    const modalStack = useStackModalStore(state => state.modalStack);
    const closeModal = useStackModalStore(state => state.closeModal);

    // 모달이 없으면 아무것도 렌더링하지 않음
    if (modalStack.length === 0) return null;

    // 모든 모달을 렌더링 (z-index를 다르게 하여 겹치도록)
    return (
        <>
            {modalStack.map((modalItem, index) => {
                const { id, modalType, modalProps } = modalItem;

                // 모달 컴포넌트 찾기
                if (!Object.keys(MODAL_COMPONENTS).includes(modalType)) {
                    console.warn(
                        `Modal component for type "${modalType}" not found.`
                    );
                    return null;
                }

                const safeModalType = modalType as ModalType;

                // 기본 z-index를 base + 스택 내 위치에 따라 증가
                const zIndexBase = 300;
                const zIndex = zIndexBase + index;

                // 모달 타입별 렌더링 로직
                switch (safeModalType) {
                    case 'CORE_VALUE_SETTINGS': {
                        interface CoreValueModalProps {
                            workspaceId: string;
                            coreValues: WorkspaceCoreValue[];
                            onSuccess?: () => void;
                            title?: string;
                            addTitle?: string;
                            addPlaceholder?: string;
                            addButtonText?: string;
                            deleteButtonText?: string;
                        }

                        // 타입 안전성을 위해 unknown으로 변환 후 다시 CoreValueModalProps로 변환
                        const typedProps =
                            modalProps as unknown as CoreValueModalProps;

                        return (
                            <div
                                key={id}
                                style={{ position: 'relative', zIndex }}
                            >
                                <CoreValueSettingsModal
                                    opened={true}
                                    onClose={() => closeModal(id)}
                                    {...typedProps}
                                />
                            </div>
                        );
                    }

                    case 'CREATE_PROJECT': {
                        interface CreateProjectProps {
                            workspaceId: string;
                        }

                        const typedProps =
                            modalProps as unknown as CreateProjectProps;

                        return (
                            <div
                                key={id}
                                style={{ position: 'relative', zIndex }}
                            >
                                <CreateProjectModal
                                    opened={true}
                                    onClose={() => closeModal(id)}
                                    {...typedProps}
                                />
                            </div>
                        );
                    }

                    case 'CONFIRMATION': {
                        interface ConfirmationProps {
                            title: string;
                            message: React.ReactNode;
                            onConfirm: () => void;
                            confirmText?: string;
                            cancelText?: string;
                            confirmButtonColor?: string;
                            loading?: boolean;
                            centered?: boolean;
                            closeOnClickOutside?: boolean;
                            closeOnEscape?: boolean;
                            onReject?: () => void;
                            overlayProps?: Record<string, unknown>;
                        }

                        // 타입 안전성을 위해 unknown으로 변환 후 다시 ConfirmationProps로 변환
                        const typedProps =
                            modalProps as unknown as ConfirmationProps;

                        return (
                            <div
                                key={id}
                                style={{ position: 'relative', zIndex }}
                            >
                                <ConfirmationModal
                                    opened={true}
                                    onClose={() => closeModal(id)}
                                    {...typedProps}
                                />
                            </div>
                        );
                    }

                    default:
                        break;
                }

                return null;
            })}
        </>
    );
};
